import { HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LababsVacationsDataDto, VacationsYearsDto } from '@app/data/dtos/lababs/lababs-vacations.dto';
import { SignedVacationDto } from '@app/data/dtos/mot/signed-vacation.dto';
import { mapLababsVacationsData, mapVacationsYearsData } from '@app/data/mappers/lababs/lababs-vacations.mapper';
import { mapDataSignedVacation } from '@app/data/mappers/mot/signed-vacations.mapper';
import { VacationsRepository } from '@app/domain/repositories/mot/vacations.repository.contract';
import { ErrorMsgEnum } from '@app/entities/enums/error/error-msg-enum';
import { IDataResponse } from '@app/entities/interfaces/data/data.interface';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { SignedVacations } from '@app/entities/models/mot/signed-vacations/signed-vacations.model';
import { Logger } from '@app/services';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MTranslateService } from '@mercadona/core/translate';
import { MRepository } from '@mercadona/core/utils/repository';

import { LababsVacations, VacationsYear } from '@/models/lababs/lababs-vacations.model';

@Injectable({
  providedIn: 'root'
})
export class VacationsRepositoryImpl extends MRepository implements VacationsRepository {
  private logger = new Logger('VacationsService');
  private onError = new Subject<ErrorMsgEnum>();
  onError$ = this.onError.asObservable();

  private readonly motUrl = '/mot';
  private readonly lababsUrl = '/timemanage/v1';

  constructor(private translateService: MTranslateService) {
    super(`${ENV.API_URL_MOT}`);
  }

  getLababsVacations(): Observable<LababsVacations[]> {
    let params = new HttpParams();
    params = params.set('lang', this.translateService.getActiveLang());

    return this.get<LababsVacationsDataDto>(`${this.lababsUrl}/calendar`, {
      params,
      context: new HttpContext().set(SECTION_KEY, 'lababs-vacations')
    }).pipe(map(mapLababsVacationsData), catchError(this.handleError));
  }

  getExtraInfo(year?: string): Observable<MSafeAny> {
    let params = new HttpParams();
    params = params.set('lang', this.translateService.getActiveLang());

    if (year) {
      params = params.set('year', year);
    }

    return this.get(`${this.motUrl}/v2/vacations/year-info`, { params }).pipe(catchError(this.handleError));
  }

  getSignedVacations(): Observable<SignedVacations[]> {
    let params = new HttpParams();
    params = params.set('lang', this.translateService.getActiveLang());

    return this.get<IDataResponse<SignedVacationDto[]>>(`${this.motUrl}/v3/signed-vacations`, { params }).pipe(
      map(mapDataSignedVacation),
      catchError(this.handleError)
    );
  }

  getAbsenceAnnualYears(): Observable<VacationsYear[]> {
    let params = new HttpParams();
    params = params.set('lang', this.translateService.getActiveLang());

    return this.get<VacationsYearsDto>(`${this.lababsUrl}/calendar/years`, {
      params,
      context: new HttpContext().set(SECTION_KEY, 'lababs-vacations')
    }).pipe(map(mapVacationsYearsData), catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<MSafeAny[]> => {
    let errorType;

    switch (error.status) {
      case ErrorCodes.LOCKED:
        errorType = ErrorMsgEnum.SERVICE_LOCKED;
        break;
      case ErrorCodes.SECTION_MAINTENANCE:
        errorType = ErrorMsgEnum.SECTION_MAINTENANCE;
        break;
      default:
        errorType = ErrorMsgEnum.SERVER_ERROR;
        break;
    }

    this.onError.next(errorType);
    this.logger.error(error);

    return of([]);
  };
}
